import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { DimensionEnums, FormEnums } from "models/enums";
import { FormFieldConstants, IconDefinitionConstants } from "utils/constants";
import { AutoCompleteExtraProps, GenericFormSection } from "models/propTypes/genericFormProps";
import { InvoiceFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { FilterQueryStateHandler } from "models/propTypes/filterPropTypes";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { InvoiceViewPaymentState } from "models/enums/invoice";
//#endregion
//#region components
import { useCustomerSearch } from "components/customer";
import { useArticleSearch } from "components/article";
import { useInvoiceService, useCompanyService, useCustomerService, useArticleService } from "serviceHooks";
//#endregion
import { DateHelper, FilterHelper, FormHelper, QueryStateHelper } from "utils/helpers";

function useInvoiceFilterDefinition() {
  const invoiceService = useInvoiceService();
  const companyService = useCompanyService();
  const customerService = useCustomerService();
  const articleService = useArticleService();

  const [suggestedArticles, setSuggestedArticles] = React.useState<AutocompleteListItem[]>([]);
  const [suggestedCustomers, setSuggestedCustomers] = React.useState<AutocompleteListItem[]>([]);
  const searchCustomers = useCustomerSearch();
  const onArticleSearch = useArticleSearch();

  const isAgentSalesActive = useSelector((store: AppState) => store.auth.selectedCompany.isAgentSalesActive);
  const isSelfInvoicingEnabled = useSelector((store: AppState) => store.auth.selectedCompany.isSelfInvoicingEnabled);

  React.useEffect(() => {
    customerService
      .getCustomerSuggestions()
      .then((res) =>
        setSuggestedCustomers(res.map((customer: any) => ({ id: customer.id ?? 0, value: customer.name ?? "" })))
      );

    invoiceService
      .getArticleSuggestions()
      .then((res) => setSuggestedArticles(res.map((article) => ({ id: article.id ?? 0, value: article.name ?? "" }))));
  }, []);

  const filterValueFormatter = (values: InvoiceFormTypes.Filter): InvoiceFormTypes.Filter => {
    if (
      values.showCreditSameMonth &&
      values.creditStatus?.length &&
      // eslint-disable-next-line eqeqeq --- since checked against different types
      !values.creditStatus.some((c) => c.id == WintSuperkollAPIModel.InvoiceCreditStatus.Credit)
    ) {
      const creditSelectionItem = invoiceService
        .getCreditStatuses()
        .find((c) => c.id === WintSuperkollAPIModel.InvoiceCreditStatus.Credit);

      if (creditSelectionItem)
        return {
          ...values,
          creditStatus: [...values.creditStatus, creditSelectionItem],
        };
    }
    return values;
  };

  const apiToReportFilterFormatter = async (
    filter: WintSuperkollAPIModel.InvoiceFilter
  ): Promise<InvoiceFormTypes.Filter> => {
    const reportFilter: InvoiceFormTypes.Filter = {};

    FilterHelper.setDateRange(
      reportFilter,
      FormFieldConstants.Invoice.Filter.InvoiceDate,
      filter.invoiceDateFrom,
      filter.invoiceDateTo
    );

    FilterHelper.setDateRange(
      reportFilter,
      FormFieldConstants.Invoice.Filter.DueDate,
      filter.dueDateFrom,
      filter.dueDateTo
    );

    if (filter.snapshotDate !== undefined) {
      reportFilter.ledgerDate = DateHelper.fromAPIDate(filter.snapshotDate);
    }

    if (filter.ownerIds?.length) {
      const owners: Promise<AutocompleteListItem[] | undefined> = companyService
        .getEmployees({ personIds: filter.ownerIds })
        .then((res) => res.map((person) => ({ id: person.id, value: person.name ?? "" })));

      reportFilter.owners = await owners;
    }

    const customerIds =
      filter.customerIds !== undefined
        ? filter.customerIds
        : filter.customerId !== undefined
        ? [filter.customerId]
        : undefined;

    if (customerIds?.length) {
      const customers = Promise.all(
        customerIds.map(async (id) => {
          const customerName = await customerService.getCustomer(id).then((c) => c.name ?? "");
          return { id: id, value: customerName };
        })
      );
      reportFilter.customers = await customers;
    }

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.Invoice.Filter.SerialNumber,
      filter.minSerialNumber,
      filter.maxSerialNumber
    );

    if (filter.statuses !== undefined) {
      reportFilter.invoiceStatuses = invoiceService
        .getInvoiceStatuses()
        .filter((s) => filter.statuses?.includes(s.id as number)) as SelectionItem[];
    }

    if (filter.paymentStatuses !== undefined) {
      const viewPaymentStates = filter.paymentStatuses.flatMap((p) => invoiceService.toViewPaymentState(p));
      reportFilter.paymentStatuses = invoiceService
        .getPaymentViewStatuses()
        .filter((s) => viewPaymentStates.includes(s.id as InvoiceViewPaymentState)) as SelectionItem[];
    }

    reportFilter.rotAndRut = filter.houseWorkInvoice;

    if (filter.articleIds?.length) {
      const articles = Promise.all(
        filter.articleIds.map(async (id) => {
          const articleName = await articleService.get(id).then((a) => a.text ?? "");
          return { id: id, value: articleName };
        })
      );
      reportFilter.articles = await articles;
    }

    if (filter.creditStatuses !== undefined) {
      reportFilter.creditStatus = invoiceService
        .getCreditStatuses()
        .filter((s) => filter.creditStatuses?.includes(s.id as number)) as SelectionItem[];
    }

    if (filter.showCreditSameMonth !== undefined) {
      reportFilter.showCreditSameMonth = filter.showCreditSameMonth;
    }

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.Invoice.Filter.InvoicedAmount,
      filter.minTotalAmount,
      filter.maxTotalAmount
    );

    FilterHelper.setNumberRange(
      reportFilter,
      FormFieldConstants.Invoice.Filter.UnpaidAmount,
      filter.minUnpaidAmount,
      filter.maxUnpaidAmount
    );

    if (filter.recurringInvoiceId !== undefined) {
      reportFilter.recurringInvoiceId = filter.recurringInvoiceId;
    }

    if (filter.dimensions !== undefined) {
      reportFilter.dimensions = filter.dimensions;
    }

    if (isAgentSalesActive) {
      reportFilter.agentInvoicesId = filter.showAgentInvoices;
    }

    if (filter.sources !== undefined) {
      reportFilter.creditStatus = invoiceService
        .getInvoiceSources()
        .filter((s) => filter.sources?.includes(s.id as number)) as SelectionItem[];
    }

    return reportFilter;
  };

  const filterFormFields: GenericFormSection<InvoiceFormTypes.Filter>[] = [
    {
      label: TranslationKeys.Common.Date,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        invoiceDate: {
          label: TranslationKeys.Common.InvoiceDate,
          type: FormEnums.FormFieldType.DATERANGE,
        },
        dueDate: {
          label: TranslationKeys.Invoice.DueDate,
          type: FormEnums.FormFieldType.DATERANGE,
        },
        ledgerDate: {
          label: TranslationKeys.Invoice.LedgerDate,
          placeholder: TranslationKeys.Invoice.LedgerDate,
          type: FormEnums.FormFieldType.DATE,
        },
      },
    },
    {
      label: TranslationKeys.Common.General,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        owners: {
          label: TranslationKeys.Common.Owner,
          type: FormEnums.FormFieldType.MULTIPLEAUTOCOMPLETE,
          extraProps: {
            items: (searchStr: string) => companyService.SearchPersonsAutoComplete(searchStr, false),
          },
        },
        customers: {
          label: TranslationKeys.Invoice.Customer,
          type: FormEnums.FormFieldType.MULTIPLEAUTOCOMPLETE,
          extraProps: {
            suggestedItems: suggestedCustomers,
            items: searchCustomers,
          },
        },
        serialNumber: {
          label: TranslationKeys.Invoice.InvoiceNumber,
          type: FormEnums.FormFieldType.NUMBERRANGE,
        },
        invoiceStatuses: {
          label: TranslationKeys.Common.Status,
          type: FormEnums.FormFieldType.MULTIPLESELECTION,
          extraProps: {
            items: invoiceService.getInvoiceStatuses(),
          },
        },
        paymentStatuses: {
          label: TranslationKeys.Invoice.PaymentStatus,
          type: FormEnums.FormFieldType.MULTIPLESELECTION,
          extraProps: {
            items: invoiceService.getPaymentViewStatuses(),
          },
        },
        sources: {
          label: TranslationKeys.Invoice.Source,
          type: FormEnums.FormFieldType.MULTIPLESELECTION,
          extraProps: {
            items: invoiceService.getInvoiceSources(),
          },
          hidden: !isSelfInvoicingEnabled,
        },
        rotAndRut: {
          label: TranslationKeys.Invoice.RotAndRut,
          type: FormEnums.FormFieldType.BOOLEANSELECTION,
        },
        articles: {
          label: TranslationKeys.Invoice.Article,
          type: FormEnums.FormFieldType.MULTIPLEAUTOCOMPLETE,
          extraProps: {
            suggestedItems: suggestedArticles,
            items: onArticleSearch,
          },
        },
      },
    },
    {
      label: TranslationKeys.Common.Agent,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        agentInvoicesId: {
          label: TranslationKeys.Invoice.AgentInvoices,
          type: FormEnums.FormFieldType.SELECTION,
          extraProps: {
            items: FormHelper.getAgentSaleInvoiceItems(),
          },
        },
      },
      hidden: !isAgentSalesActive,
    },
    {
      label: TranslationKeys.Invoice.CreditInvoices,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        creditStatus: {
          label: TranslationKeys.Invoice.CreditInvoices,
          type: FormEnums.FormFieldType.MULTIPLESELECTION,
          extraProps: {
            items: invoiceService.getCreditStatuses(),
          } as AutoCompleteExtraProps<InvoiceFormTypes.Filter>,
        },
        showCreditSameMonth: {
          label: TranslationKeys.Invoice.ShowCreditSameMonth,
          type: FormEnums.FormFieldType.TOGGLE,
          hidden: (values: InvoiceFormTypes.Filter) => !!values.ledgerDate,
        },
      },
    },
    {
      label: TranslationKeys.Common.Amount,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        invoicedAmount: {
          label: TranslationKeys.Invoice.InvoicedAmount,
          type: FormEnums.FormFieldType.CURRENCYRANGE,
        },
        unpaidAmount: {
          label: TranslationKeys.Invoice.UnpaidAmount,
          type: FormEnums.FormFieldType.CURRENCYRANGE,
        },
      },
    },
    {
      label: TranslationKeys.Common.Tags,
      type: FormEnums.FormSectionType.EXPANDABLESECTION,
      icon: IconDefinitionConstants.WSliders,
      innerFields: {
        dimensions: {
          label: TranslationKeys.Common.Tags,
          type: FormEnums.FormFieldType.DIMENSIONMULTIPLEINPUTSELECTOR,
          extraProps: {
            type: DimensionEnums.DimensionCategory.Invoice,
          },
        },
      },
    },
  ];

  const filterQueryStateHandler: FilterQueryStateHandler<InvoiceFormTypes.Filter> = {
    invoiceDate: QueryStateHelper.dateRangeQueryEncoder(FormFieldConstants.Invoice.Filter.InvoiceDate),
    dueDate: QueryStateHelper.dateRangeQueryEncoder(FormFieldConstants.Invoice.Filter.DueDate),
    ledgerDate: QueryStateHelper.dateQueryEncoder(FormFieldConstants.Invoice.Filter.LedgerDate),
    owners: QueryStateHelper.multipleAutoCompleteItemsQueryEncoder(FormFieldConstants.Invoice.Filter.Owners),
    customers: QueryStateHelper.multipleAutoCompleteItemsQueryEncoder(FormFieldConstants.Invoice.Filter.Customers),
    serialNumber: QueryStateHelper.numberRangeQueryEncoder(FormFieldConstants.Invoice.Filter.SerialNumber),
    invoiceStatuses: QueryStateHelper.selectionItemsQueryEncoder(
      FormFieldConstants.Invoice.Filter.InvoiceStatuses,
      invoiceService.getInvoiceStatuses().map((i) => ({
        id: i.id ?? 0,
        value: i.value,
        description: i.description,
        data: i.data,
      }))
    ),
    paymentStatuses: QueryStateHelper.selectionItemsQueryEncoder(
      FormFieldConstants.Invoice.Filter.PaymentStatuses,
      invoiceService.getPaymentViewStatuses().map((i) => ({
        id: i.id ?? 0,
        value: i.value,
        description: i.description,
        data: i.data,
      }))
    ),
    rotAndRut: QueryStateHelper.booleanQueryEncoder(FormFieldConstants.Invoice.Filter.RotAndRut),
    articles: QueryStateHelper.multipleAutoCompleteItemsQueryEncoder(FormFieldConstants.Invoice.Filter.Articles),
    creditStatus: QueryStateHelper.selectionItemsQueryEncoder(
      FormFieldConstants.Invoice.Filter.CreditStatus,
      invoiceService.getCreditStatuses()
    ),
    showCreditSameMonth: QueryStateHelper.booleanQueryEncoder(FormFieldConstants.Invoice.Filter.ShowCreditSameMonth),
    invoicedAmount: QueryStateHelper.numberRangeQueryEncoder(FormFieldConstants.Invoice.Filter.InvoicedAmount),
    unpaidAmount: QueryStateHelper.numberRangeQueryEncoder(FormFieldConstants.Invoice.Filter.UnpaidAmount),
    agentInvoicesId: QueryStateHelper.numberQueryEncoder(FormFieldConstants.Invoice.Filter.AgentInvoicesId),
    dimensions: QueryStateHelper.dimensionsQueryEncoder(FormFieldConstants.Invoice.Filter.Dimensions),
    sources: QueryStateHelper.selectionItemsQueryEncoder(
      FormFieldConstants.Invoice.Filter.Sources,
      invoiceService.getInvoiceSources()
    ),
  };

  return { filterFormFields, filterValueFormatter, filterQueryStateHandler, apiToReportFilterFormatter };
}

export default useInvoiceFilterDefinition;
